import { type LiveTerminal, TERMINAL_ENDPOINTS } from '@iws/backoffice-api'
import type { DeviceModel as CatalogModel, DeviceSetup } from '@iws/pos-devices'

import { Model } from '@/clients/http'
import { stringifyGraph } from '@/clients/http/utils'
import { createResourceApi } from '@/http/models/Resource'

export interface Terminal {
  _id: string
  name: string
  unique_no: number
  branch?: { _id: string; name: string } | null
  device_setup: DeviceSetup | null
  // Fields the Terminals page asks for in its graph (checklist C23).
  branch_id?: string
  description?: string
  status?: 'active' | 'inactive'
  is_open?: boolean
  cash_box?: number
}

type ErrorBody = { message?: string }

const crud = createResourceApi<Terminal>('/api/backoffice/terminal', 'TERMINAL')

// Settings → POS → Till devices (POS.md §18.9) and the Terminals page (checklist C23).
export class TerminalModel extends Model {
  public static readonly correlations = {
    GET_LIST: 'GET_TERMINAL_LIST',
    SAVE_DEVICE_SETUP: 'SAVE_TERMINAL_DEVICE_SETUP',
    GET_DEVICE_MODELS: 'GET_DEVICE_CATALOG',
    GET_LIVE: 'GET_TERMINAL_LIVE',
    REQUEST_CLOSE: 'REQUEST_TERMINAL_SHIFT_CLOSE',
  }

  /** Each terminal with its open shift, drawer, sales today and last sync (POS.md §18, Terminals page). */
  public static readonly getLive = async (branchId?: string) =>
    this.createHttpRequest<{ result: LiveTerminal[] }, ErrorBody>({
      correlationKey: this.correlations.GET_LIVE,
      payload: {
        method: 'get',
        url: this.createEndpoint({ url: TERMINAL_ENDPOINTS.live, query: branchId ? { branch_id: branchId } : {} }),
        useBearerToken: true,
      },
    })

  /** Asks the device to close an open shift; the device closes it after its next sync. */
  public static readonly requestClose = async (shiftId: string) =>
    this.createHttpRequest<{ result: unknown }, ErrorBody>({
      correlationKey: this.correlations.REQUEST_CLOSE,
      payload: {
        method: 'post',
        url: this.createEndpoint({ url: TERMINAL_ENDPOINTS.requestClose, params: { id: shiftId } }),
        useBearerToken: true,
      },
    })

  public static readonly getList = async () =>
    this.createHttpRequest<{ result: Terminal[]; count: number }, ErrorBody>({
      correlationKey: this.correlations.GET_LIST,
      payload: {
        method: 'get',
        url: this.createEndpoint({
          url: '/api/backoffice/terminal',
          query: { length: 200, graph_enc: stringifyGraph({ _id: true, name: true, unique_no: true, device_setup: true, branch: { name: true } }) },
        }),
        useBearerToken: true,
      },
    })

  /** Every terminal with its branch, cash box and open state (Terminals page). */
  public static readonly getAll = async () =>
    crud.getList({
      length: 200,
      graph_enc: stringifyGraph({
        _id: true, name: true, unique_no: true, branch_id: true, description: true, status: true, is_open: true, cash_box: true,
        branch: { name: true },
      }),
    })

  public static readonly getPage = crud.getList
  public static readonly create = crud.create
  public static readonly update = crud.update
  public static readonly remove = crud.remove

  public static readonly saveDeviceSetup = async (id: string, setup: DeviceSetup) =>
    this.createHttpRequest<{ result: DeviceSetup }, ErrorBody>({
      correlationKey: this.correlations.SAVE_DEVICE_SETUP,
      payload: {
        method: 'put',
        url: this.createEndpoint({ url: '/api/backoffice/terminal/:id/device-setup', params: { id } }),
        body: { devices: setup.devices, roles: setup.roles, options: setup.options ?? {} },
        useBearerToken: true,
      },
    })

  /** The System Admin device catalog (active models). */
  public static readonly getDeviceModels = async () =>
    this.createHttpRequest<{ result: CatalogModel[] }, ErrorBody>({
      correlationKey: this.correlations.GET_DEVICE_MODELS,
      payload: { method: 'get', url: this.createEndpoint({ url: '/api/backoffice/device/models' }), useBearerToken: true },
    })
}
